import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ImageSquare } from "@phosphor-icons/react";
import { DesignProgress } from "@/components/designs/DesignProgress";
import { DesignFilters } from "@/components/designs/DesignFilters";
import { DesignCard } from "@/components/designs/DesignCard";
import { DesignDetailDialog } from "@/components/designs/DesignDetailDialog";
import { DesignFailedList } from "@/components/designs/DesignFailedList";
import { LensDialog } from "@/components/designs/LensDialog";
import { Camera, PresentationChart } from "@phosphor-icons/react";
import { CategoryTabs } from "@/components/designs/Categories";
import { AiCategorizeBar } from "@/components/designs/AiCategorizeBar";
import { ShowMode } from "@/components/designs/ShowMode";

const PAGE = 60;

export default function DesignsPage() {
  const { user } = useAuth();
  const allowed = user?.role === "admin" || user?.role === "secondary_admin";
  const [p, setP] = useState({ ready: 0, pending: 0, failed: 0, total: 0, backfill_enabled: true });
  const [f, setF] = useState({ q: "", customer: "", size: "" });
  const [tab, setTab] = useState("ready");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lensOpen, setLensOpen] = useState(false);
  const [cats, setCats] = useState({ categories: [], all: 0 });
  const [sel, setSel] = useState([]);
  const [unique, setUnique] = useState(true);
  const [review, setReview] = useState(false);
  const [aiS, setAiS] = useState({ done: 0, ready_total: 0, queued: 0, failed: 0, review: 0, locked: 0 });
  const [show, setShow] = useState(false);
  const loadCats = useCallback(() => {
    api.get("/designs/categories", { params: { unique } }).then(({ data }) => setCats(data)).catch(() => {});
    api.get("/designs/categorize/status").then(({ data }) => setAiS(data)).catch(() => {});
  }, [unique]);

  const loadProgress = useCallback(() => api.get("/designs/progress").then(({ data }) => setP(data)).catch(() => {}), []);
  const load = useCallback(async (skip = 0) => {
    setLoading(true);
    try {
      const params = tab === "failed" ? { status: "failed", limit: 200 }
        : { status: "ready", limit: PAGE, skip, ...f, cats: sel.join(","), unique: unique && !review, review };
      const { data } = await api.get("/designs", { params });
      setRows((r) => (skip ? [...r, ...data.items] : data.items));
      setTotal(data.total);
      if (data.serial_hit && data.items.length === 1) setOpen(data.items[0]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  }, [tab, f, sel, unique, review]);

  useEffect(() => { if (allowed) { const t = setTimeout(() => load(0), 300); return () => clearTimeout(t); } }, [load, allowed]);
  useEffect(() => {
    if (!allowed) return;
    loadProgress();
    api.get("/designs/customers").then(({ data }) => setCustomers(data)).catch(() => {});
    loadCats();
    const t = setInterval(() => { loadProgress(); loadCats(); }, 10000);
    return () => clearInterval(t);
  }, [allowed, loadProgress, loadCats]);

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); await loadProgress(); await load(0); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const toggle = () => act(() => api.post("/designs/backfill", { enabled: !p.backfill_enabled }));
  const retry = (id) => act(async () => {
    const { data } = await api.post(`/designs/${id}/retry`);
    data.status === "ready" ? toast.success("Image ban gayi") : toast.error(data.error || "Phir se fail hua");
  });
  const runAi = (limit = 100) => act(async () => {
    const { data } = await api.post("/designs/categorize", { limit });
    toast.success(`${data.queued} designs AI line me`);
    loadCats();
  });
  const saved = (d) => { setOpen(d); setRows((r) => r.map((x) => (x.id === d.id ? { ...x, ...d } : x))); loadCats(); };
  const retryAll = () => act(async () => {
    const { data } = await api.post("/designs/retry-failed");
    toast.success(`${data.queued} files dobara line me lagi`);
  });

  if (!allowed) return <div className="p-10 text-center text-slate-400" data-testid="designs-no-access">Ye page sirf Admin / Secondary Admin ke liye hai.</div>;

  return (
    <div className="space-y-5" data-testid="designs-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-100 flex items-center gap-2">
            <ImageSquare size={28} className="text-orange-400" /> DESIGNS LIBRARY
          </h1>
          <div className="text-sm text-slate-400">Har cutting DXF ki image — search karo, dekho, original DXF download karo.</div>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && p.lens_enabled && (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => act(() => api.put("/designs/lens-toggle", { on: !p.lens_on }))}
              className={"rounded-sm h-11 bg-slate-950 " + (p.lens_on ? "border-emerald-500/60 text-emerald-300" : "border-slate-700 text-slate-400")}
              data-testid="designs-lens-toggle">LENS {p.lens_on ? "ON" : "OFF"}</Button>
          )}
          <Button onClick={() => setLensOpen(true)} className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white h-11 px-5" data-testid="designs-lens-btn">
            <Camera size={18} className="mr-2" weight="bold" /> LENS
          </Button>
        </div>
      </div>
      <DesignProgress p={p} onToggle={toggle} busy={busy} />
      <div className="flex gap-2">
        {[["ready", `READY (${p.ready})`], ["failed", `FAILED (${p.failed + (p.invalid || 0)})`]].map(([k, l]) => (
          <Button key={k} size="sm" onClick={() => setTab(k)} data-testid={`designs-tab-${k}`}
            className={"rounded-sm " + (tab === k ? "bg-orange-500 hover:bg-orange-600 text-white" : "bg-slate-900 border border-slate-700 text-slate-300 hover:bg-slate-800")}>
            {l}
          </Button>
        ))}
      </div>
      {tab === "failed" ? (
        <DesignFailedList rows={rows} onRetry={retry} onRetryAll={retryAll} busy={busy} />
      ) : (
        <>
          <AiCategorizeBar s={aiS} onRun={() => runAi(100)} onRunAll={() => window.confirm("Baaki saare designs ko AI category dein? (lagbhag ₹150–250, ek baar)") && runAi(2000)} busy={busy} />
          <CategoryTabs cats={cats.categories} counts={Object.fromEntries(cats.categories.map((c) => [c.key, c.count]))} allCount={cats.all}
            selected={sel} setSelected={setSel} unique={unique} setUnique={setUnique} review={review} setReview={setReview} reviewCount={aiS.review} />
          <DesignFilters f={f} setF={setF} customers={customers} />
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs text-slate-400 font-mono-num" data-testid="designs-result-count">{total} designs mile</div>
            <Button size="sm" variant="outline" disabled={!rows.length} onClick={() => setShow(true)}
              className="rounded-sm border-slate-700 bg-slate-950" data-testid="designs-show-mode-btn">
              <PresentationChart size={16} className="mr-1" /> SHOW MODE
            </Button>
          </div>
          {!loading && rows.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm border border-slate-800 rounded-sm" data-testid="designs-empty">Koi design nahi mila.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="designs-grid">
              {rows.map((d) => <DesignCard key={d.id} d={d} onOpen={setOpen} />)}
            </div>
          )}
          {rows.length < total && (
            <div className="text-center">
              <Button variant="outline" disabled={loading} onClick={() => load(rows.length)}
                className="rounded-sm border-slate-700 bg-slate-950" data-testid="designs-load-more">LOAD MORE</Button>
            </div>
          )}
        </>
      )}
      <DesignDetailDialog d={open} onClose={() => setOpen(null)} onSaved={saved} />
      {show && <ShowMode items={rows} onClose={() => setShow(false)} />}
      {lensOpen && <LensDialog open={lensOpen} onClose={() => setLensOpen(false)} threshold={p.lens_threshold ?? 85}
        onThreshold={(v) => setP((x) => ({ ...x, lens_threshold: v }))} onOpenDesign={setOpen} />}
    </div>
  );
}
